import type { ApiClient, RequestOptions } from '../../api/http';
import type { ApiResponse } from '../../api/contract';
import type { CommissionRulesRepository } from './repository';
import type {
  CancelRuleInput,
  CommissionRuleCreateInput,
  CommissionRuleListQuery,
  CommissionRulePreviewInput,
  PublishRuleInput,
} from './types';
import { bangkokWallTimeToRfc3339 } from './types';

type RuleResponse = ApiResponse<'/api/v1/commission-rules/{id}', 'get'>;
type RuleListResponse = ApiResponse<'/api/v1/commission-rules', 'get'>;
type PreviewResponse = ApiResponse<'/api/v1/commission-rules/preview', 'post'>;

export function createRealCommissionRulesRepository(
  client: ApiClient,
  accessToken: string | null,
): CommissionRulesRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async list(query, signal) {
      const result = await client.get<RuleListResponse>('/api/v1/commission-rules', {
        ...auth,
        query: listQuery(query),
        signal,
      });
      return {
        items: result.data.data,
        total: result.data.pagination.total,
        requestId: result.requestId,
      };
    },
    async get(ruleId, signal) {
      const result = await client.get<RuleResponse>(rulePath(ruleId), { ...auth, signal });
      return result.data.data;
    },
    async preview(input: CommissionRulePreviewInput) {
      const result = await client.post<PreviewResponse>(
        '/api/v1/commission-rules/preview',
        input,
        auth,
      );
      return result.data.data;
    },
    async create(input: CommissionRuleCreateInput, idempotencyKey: string) {
      const result = await client.post<RuleResponse>('/api/v1/commission-rules', input, {
        ...auth,
        idempotencyKey: requireUuid(idempotencyKey),
      });
      return { data: result.data.data, requestId: result.requestId };
    },
    async publish(ruleId: string, input: PublishRuleInput, idempotencyKey: string) {
      const result = await client.post<RuleResponse>(`${rulePath(ruleId)}/publish`, input, {
        ...auth,
        idempotencyKey: requireUuid(idempotencyKey),
      });
      return { data: result.data.data, requestId: result.requestId };
    },
    async cancel(ruleId: string, input: CancelRuleInput, idempotencyKey: string) {
      const result = await client.post<RuleResponse>(`${rulePath(ruleId)}/cancel`, input, {
        ...auth,
        idempotencyKey: requireUuid(idempotencyKey),
      });
      return { data: result.data.data, requestId: result.requestId };
    },
  };
}

function listQuery(query: CommissionRuleListQuery): Readonly<Record<string, unknown>> {
  const filters = {
    ...query.filters,
    effectiveFrom: bangkokWallTimeToRfc3339(query.filters.effectiveFrom),
    effectiveTo: bangkokWallTimeToRfc3339(query.filters.effectiveTo),
  };
  return Object.fromEntries(
    Object.entries({
      page: query.page,
      pageSize: query.pageSize,
      sort: query.sort,
      order: query.order,
      ...filters,
    }).filter(([, value]) => value !== ''),
  );
}

function rulePath(ruleId: string): string {
  return `/api/v1/commission-rules/${encodeURIComponent(ruleId)}`;
}

function requireUuid(value: string): string {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value)) {
    throw new Error('A UUID v4 idempotency key is required');
  }
  return value;
}
